/**
 * MicroTec ROI Calculator App
 * Clean, event-delegated controller for [data-roi-calc] roots.
 * P0: modal focus trap/Escape, real button activation, gated download, no alerts.
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    return Number(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function formatCurrency(num) {
    return '$' + formatNumber(num);
  }

  function calculateFinancials(params) {
    var presses = Math.max(1, params.presses || 1);
    var items = params.items || 0;
    var price = params.price || 0;
    var cogs = params.cogs || 0;
    var labor = params.labor || 0;
    var hoursPerDay = params.hoursPerDay || 8;
    var machinePrice = params.machinePrice || 0;
    var capacityPerHour = params.capacityPerHour || 0;

    var totalMachineCost = machinePrice * presses;
    var totalCapacityPerHour = capacityPerHour * presses;
    var revenue = items * price;
    var totalCogs = items * cogs;
    var hoursNeeded = totalCapacityPerHour > 0 ? items / totalCapacityPerHour : 0;
    var laborCost = hoursNeeded * labor;
    var totalCosts = totalCogs + laborCost + totalMachineCost;
    var profit = revenue - totalCosts;
    var margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    var daysNeeded = hoursPerDay > 0 ? hoursNeeded / hoursPerDay : 0;
    var dailyProfit = daysNeeded > 0 && profit >= 0 ? profit / daysNeeded : 0;
    var daysToROI = dailyProfit > 0 ? totalMachineCost / dailyProfit : 0;

    return {
      revenue: revenue,
      totalCosts: totalCosts,
      profit: profit,
      margin: margin,
      hoursNeeded: hoursNeeded,
      daysNeeded: daysNeeded,
      dailyProfit: dailyProfit,
      daysToROI: daysToROI,
      totalMachineCost: totalMachineCost,
      totalCapacityPerHour: totalCapacityPerHour,
      totalCogs: totalCogs,
      laborCost: laborCost,
    };
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function RoiCalculatorApp(root) {
    this.root = root;
    this.selectedMachine = null;
    this.returnFocus = null;
    this.boundKeydown = this.onDocumentKeydown.bind(this);
    this.shopUrl = (root.dataset.shopUrl || window.location.origin || '').replace(/\/$/, '');
    this.defaultProductId = root.dataset.defaultProductId || '';
    this.primaryColor = root.dataset.primaryColor || '#3780C3';
    this.successColor = root.dataset.successColor || '#22A200';
    this.sortBy = 'name';
    this.sortDir = 'asc';

    this.els = {
      openPicker: root.querySelector('[data-roi-open-picker]'),
      selectLabel: root.querySelector('[data-roi-select-label]'),
      machineInfo: root.querySelector('[data-roi-machine-info]'),
      modal: root.querySelector('[data-roi-modal]'),
      modalDialog: root.querySelector('[data-roi-modal-dialog]'),
      closeModal: root.querySelector('[data-roi-close-modal]'),
      search: root.querySelector('[data-roi-search]'),
      sortBy: root.querySelector('[data-roi-sort-by]'),
      sortDir: root.querySelector('[data-roi-sort-dir]'),
      sortDirIcon: root.querySelector('[data-roi-sort-dir-icon]'),
      sortDirLabel: root.querySelector('[data-roi-sort-dir-label]'),
      productList: root.querySelector('[data-roi-product-list]'),
      download: root.querySelector('[data-roi-download]'),
      status: root.querySelector('[data-roi-status]'),
      lossWarning: root.querySelector('[data-roi-loss-warning]'),
      metrics: {
        revenue: root.querySelector('[data-roi-metric="revenue"]'),
        costs: root.querySelector('[data-roi-metric="costs"]'),
        days: root.querySelector('[data-roi-metric="days"]'),
        hours: root.querySelector('[data-roi-metric="hours"]'),
        profit: root.querySelector('[data-roi-metric="profit"]'),
        margin: root.querySelector('[data-roi-metric="margin"]'),
        roi: root.querySelector('[data-roi-metric="roi"]'),
        daily: root.querySelector('[data-roi-metric="daily"]'),
      },
      inputs: {},
      sliders: {},
    };

    var fields = ['presses', 'items', 'price', 'cogs', 'labor', 'hours'];
    for (var i = 0; i < fields.length; i++) {
      var name = fields[i];
      this.els.inputs[name] = root.querySelector('[data-roi-input="' + name + '"]');
      this.els.sliders[name] = root.querySelector('[data-roi-slider="' + name + '"]');
    }

    this.bindEvents();
    this.updateSortDirButton();
    this.applySort();
    this.autoSelectMachine();
    this.calculate();
  };

  RoiCalculatorApp.prototype.bindEvents = function () {
    var self = this;
    var root = this.root;

    root.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-roi-open-picker]');
      if (openBtn && root.contains(openBtn)) {
        e.preventDefault();
        self.openModal();
        return;
      }

      var closeBtn = e.target.closest('[data-roi-close-modal]');
      if (closeBtn && root.contains(closeBtn)) {
        e.preventDefault();
        self.closeModal();
        return;
      }

      var product = e.target.closest('[data-roi-product]');
      if (product && root.contains(product)) {
        e.preventDefault();
        self.selectMachine(product);
        return;
      }

      var download = e.target.closest('[data-roi-download]');
      if (download && root.contains(download)) {
        e.preventDefault();
        self.downloadReport();
        return;
      }

      var sortDirBtn = e.target.closest('[data-roi-sort-dir]');
      if (sortDirBtn && root.contains(sortDirBtn)) {
        e.preventDefault();
        self.toggleSortDirection();
        return;
      }

      if (e.target === self.els.modal) {
        self.closeModal();
      }
    });

    root.addEventListener('input', function (e) {
      var slider = e.target.closest('[data-roi-slider]');
      if (slider && root.contains(slider)) {
        self.syncField(slider.dataset.roiSlider, slider.value, 'slider');
        return;
      }

      var search = e.target.closest('[data-roi-search]');
      if (search && root.contains(search)) {
        self.filterProducts(search.value);
      }
    });

    root.addEventListener('change', function (e) {
      var input = e.target.closest('[data-roi-input]');
      if (input && root.contains(input)) {
        self.syncField(input.dataset.roiInput, input.value, 'input');
        return;
      }

      var sortBy = e.target.closest('[data-roi-sort-by]');
      if (sortBy && root.contains(sortBy)) {
        self.sortBy = sortBy.value || 'name';
        self.applySort();
      }
    });

    // Don't let range inputs steal trackpad scroll
    root.querySelectorAll('input[type="range"]').forEach(function (range) {
      range.addEventListener(
        'wheel',
        function (e) {
          e.preventDefault();
          window.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
        },
        { passive: false }
      );
    });
  };

  RoiCalculatorApp.prototype.syncField = function (name, value, source) {
    var num = parseFloat(value);
    if (isNaN(num)) num = 0;

    var input = this.els.inputs[name];
    var slider = this.els.sliders[name];
    if (source !== 'input' && input) input.value = num;
    if (source !== 'slider' && slider) slider.value = num;
    if (source === 'input' && slider) slider.value = num;
    if (source === 'slider' && input) input.value = num;

    this.calculate();
  };

  RoiCalculatorApp.prototype.getParams = function () {
    var read = function (el, fallback) {
      if (!el) return fallback;
      var n = parseFloat(el.value);
      return isNaN(n) ? fallback : n;
    };

    return {
      presses: read(this.els.sliders.presses || this.els.inputs.presses, 1),
      items: read(this.els.sliders.items || this.els.inputs.items, 0),
      price: read(this.els.sliders.price || this.els.inputs.price, 0),
      cogs: read(this.els.sliders.cogs || this.els.inputs.cogs, 0),
      labor: read(this.els.sliders.labor || this.els.inputs.labor, 0),
      hoursPerDay: read(this.els.sliders.hours || this.els.inputs.hours, 8),
      machinePrice: this.selectedMachine ? this.selectedMachine.price : 0,
      capacityPerHour: this.selectedMachine ? this.selectedMachine.capacity : 0,
    };
  };

  RoiCalculatorApp.prototype.setStatus = function (message, isError) {
    if (!this.els.status) return;
    this.els.status.textContent = message || '';
    this.els.status.classList.toggle('is-error', !!isError && !!message);
  };

  RoiCalculatorApp.prototype.setDownloadEnabled = function (enabled) {
    if (!this.els.download) return;
    this.els.download.disabled = !enabled;
    this.els.download.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  };

  RoiCalculatorApp.prototype.selectMachine = function (element) {
    if (!element || !element.dataset) return;

    var capacity = parseFloat(element.dataset.capacity);
    this.selectedMachine = {
      id: parseInt(element.dataset.productId, 10) || 0,
      handle: element.dataset.handle || '',
      title: element.dataset.title || 'Unknown Machine',
      price: (parseFloat(element.dataset.price) || 0) / 100,
      capacity: isNaN(capacity) ? 0 : capacity,
      image: element.dataset.image || '',
    };

    if (this.els.machineInfo) {
      var imgHtml = this.selectedMachine.image
        ? '<img src="' +
          escapeHtml(this.selectedMachine.image) +
          '" class="roi-calc__machine-thumb" alt="' +
          escapeHtml(this.selectedMachine.title) +
          '" width="90" height="90">'
        : '';
      var productUrl = this.shopUrl + '/products/' + this.selectedMachine.handle;
      this.els.machineInfo.innerHTML =
        imgHtml +
        '<div class="roi-calc__machine-body">' +
        '<div class="roi-calc__machine-details">' +
        '<strong>' +
        escapeHtml(this.selectedMachine.title) +
        '</strong><br>' +
        'Price: ' +
        formatCurrency(this.selectedMachine.price) +
        '<br>' +
        'Output: ' +
        formatNumber(this.selectedMachine.capacity) +
        ' items/hour' +
        '</div>' +
        '<a href="' +
        escapeHtml(productUrl) +
        '" target="_blank" rel="noopener" class="roi-calc__view-product">View Product Page</a>' +
        '</div>';
      this.els.machineInfo.classList.add('is-active');
    }

    if (this.els.selectLabel) {
      this.els.selectLabel.textContent = this.selectedMachine.title;
    }

    this.setDownloadEnabled(true);
    this.setStatus('');
    this.closeModal();
    this.calculate();
  };

  RoiCalculatorApp.prototype.filterProducts = function (query) {
    var term =
      query !== undefined && query !== null
        ? String(query).toLowerCase().trim()
        : this.els.search
          ? this.els.search.value.toLowerCase().trim()
          : '';
    var items = this.root.querySelectorAll('[data-roi-product]');
    items.forEach(function (item) {
      var title = (item.dataset.title || '').toLowerCase();
      item.classList.toggle('is-hidden', term !== '' && title.indexOf(term) === -1);
    });
  };

  RoiCalculatorApp.prototype.toggleSortDirection = function () {
    this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    this.updateSortDirButton();
    this.applySort();
  };

  RoiCalculatorApp.prototype.updateSortDirButton = function () {
    var isAsc = this.sortDir === 'asc';
    if (this.els.sortDir) {
      this.els.sortDir.dataset.direction = this.sortDir;
      this.els.sortDir.setAttribute(
        'aria-label',
        isAsc ? 'Sort ascending. Click to sort descending.' : 'Sort descending. Click to sort ascending.'
      );
      this.els.sortDir.setAttribute('title', isAsc ? 'Ascending' : 'Descending');
    }
    if (this.els.sortDirIcon) {
      this.els.sortDirIcon.textContent = isAsc ? '↑' : '↓';
    }
    if (this.els.sortDirLabel) {
      this.els.sortDirLabel.textContent = isAsc ? 'Asc' : 'Desc';
    }
  };

  RoiCalculatorApp.prototype.getSortValue = function (item, key) {
    if (key === 'price') {
      var price = parseFloat(item.dataset.price);
      return isNaN(price) ? 0 : price;
    }
    if (key === 'capacity') {
      var capacity = parseFloat(item.dataset.capacity);
      return isNaN(capacity) ? 0 : capacity;
    }
    return (item.dataset.title || '').toLowerCase();
  };

  RoiCalculatorApp.prototype.applySort = function () {
    var list = this.els.productList;
    if (!list) return;

    if (this.els.sortBy && this.els.sortBy.value) {
      this.sortBy = this.els.sortBy.value;
    }

    var key = this.sortBy || 'name';
    var dir = this.sortDir === 'desc' ? -1 : 1;
    var self = this;
    var items = Array.prototype.slice.call(list.querySelectorAll('[data-roi-product]'));

    items.sort(function (a, b) {
      var av = self.getSortValue(a, key);
      var bv = self.getSortValue(b, key);

      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true }) * dir;
      }
      if (av === bv) {
        // Stable-ish secondary sort by name
        return String(a.dataset.title || '').localeCompare(String(b.dataset.title || ''), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      }
      return av < bv ? -1 * dir : 1 * dir;
    });

    items.forEach(function (item) {
      list.appendChild(item);
    });

    this.filterProducts();
  };

  RoiCalculatorApp.prototype.roiLabel = function (results, hoursPerDay) {
    if (!this.selectedMachine) return 'N/A';
    if (results.profit < 0) return 'N/A (Loss-making)';
    if (results.daysToROI <= 0) return 'N/A';
    if (results.daysToROI < 1) {
      return formatNumber(results.daysToROI * hoursPerDay) + ' Hours';
    }
    if (results.daysToROI <= 60) {
      return formatNumber(results.daysToROI) + ' Days';
    }
    return formatNumber(results.daysToROI / 30) + ' Months';
  };

  RoiCalculatorApp.prototype.calculate = function () {
    var params = this.getParams();
    var results = calculateFinancials(params);
    var hasMachine = !!this.selectedMachine;
    var isLoss = hasMachine && results.profit < 0;
    var m = this.els.metrics;

    if (m.revenue) m.revenue.textContent = formatCurrency(results.revenue);
    if (m.costs) m.costs.textContent = formatCurrency(results.totalCosts);
    if (m.profit) {
      m.profit.textContent = formatCurrency(results.profit);
      m.profit.classList.toggle('is-loss', isLoss);
      m.profit.classList.toggle('is-good', hasMachine && !isLoss && results.margin >= 10);
    }
    if (m.margin) m.margin.textContent = formatNumber(results.margin) + '%';
    if (m.hours) {
      m.hours.textContent = hasMachine ? formatNumber(results.hoursNeeded) + ' hrs' : 'N/A';
    }
    if (m.days) {
      m.days.textContent = hasMachine ? formatNumber(results.daysNeeded) + ' days' : 'N/A';
    }
    if (m.daily) {
      m.daily.textContent = hasMachine ? formatCurrency(results.dailyProfit) : 'N/A';
    }
    if (m.roi) m.roi.textContent = this.roiLabel(results, params.hoursPerDay);

    if (this.els.lossWarning) {
      if (isLoss) {
        this.els.lossWarning.innerHTML =
          '<strong>Loss warning:</strong> This mix loses ' +
          formatCurrency(Math.abs(results.profit)) +
          '. Consider raising prices or reducing costs.';
        this.els.lossWarning.classList.add('is-visible');
      } else {
        this.els.lossWarning.textContent = '';
        this.els.lossWarning.classList.remove('is-visible');
      }
    }

    this.setDownloadEnabled(hasMachine);
  };

  RoiCalculatorApp.prototype.getFocusable = function () {
    if (!this.els.modalDialog) return [];
    return Array.prototype.slice.call(this.els.modalDialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
      // offsetParent is null for position:fixed nodes — use client rects instead
      if (el.disabled || el.getAttribute('aria-hidden') === 'true') return false;
      var style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      return el.getClientRects().length > 0;
    });
  };

  RoiCalculatorApp.prototype.openModal = function () {
    if (!this.els.modal) return;
    this.returnFocus = document.activeElement;
    this.els.modal.hidden = false;
    this.els.modal.classList.add('is-open');
    if (this.els.openPicker) this.els.openPicker.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.boundKeydown);

    var self = this;
    requestAnimationFrame(function () {
      if (self.els.search) self.els.search.focus();
      else {
        var focusable = self.getFocusable();
        if (focusable[0]) focusable[0].focus();
      }
    });
  };

  RoiCalculatorApp.prototype.closeModal = function () {
    if (!this.els.modal) return;
    this.els.modal.classList.remove('is-open');
    this.els.modal.hidden = true;
    if (this.els.openPicker) this.els.openPicker.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.boundKeydown);

    if (this.returnFocus && typeof this.returnFocus.focus === 'function') {
      this.returnFocus.focus();
    }
    this.returnFocus = null;
  };

  RoiCalculatorApp.prototype.onDocumentKeydown = function (e) {
    if (!this.els.modal || !this.els.modal.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeModal();
      return;
    }

    if (e.key !== 'Tab') return;

    var focusable = this.getFocusable();
    if (!focusable.length) {
      e.preventDefault();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || !this.els.modalDialog.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  RoiCalculatorApp.prototype.autoSelectMachine = function () {
    var list = this.els.productList;
    if (!list) return;

    var target = null;
    if (this.defaultProductId && this.defaultProductId !== 'null') {
      target = list.querySelector('[data-product-id="' + this.defaultProductId + '"]');
    }
    if (!target) {
      target = list.querySelector('[data-roi-product]');
    }
    if (target) this.selectMachine(target);
  };

  RoiCalculatorApp.prototype.downloadReport = function () {
    if (!this.selectedMachine) {
      this.setStatus('Select a machine before downloading a report.', true);
      if (this.els.openPicker) this.els.openPicker.focus();
      return;
    }

    var params = this.getParams();
    var results = calculateFinancials(params);
    var productUrl = this.shopUrl + '/products/' + this.selectedMachine.handle;
    var primary = this.primaryColor;
    var success = this.successColor;
    var roiText = this.roiLabel(results, params.hoursPerDay);
    var title = escapeHtml(this.selectedMachine.title);

    var html =
      '<!DOCTYPE html><html><head><title>ROI Report - ' +
      title +
      '</title><meta charset="UTF-8"><style>' +
      '@page{size:A4;margin:15mm}*{margin:0;padding:0;box-sizing:border-box}' +
      'body{font-family:Inter,Arial,sans-serif;padding:15px;max-width:210mm;margin:0 auto;font-size:11px;line-height:1.45;color:#222}' +
      '.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:15px;padding-bottom:10px;border-bottom:3px solid ' +
      primary +
      '}' +
      '.product-image{width:120px;height:120px;object-fit:contain;border:2px solid #ddd;border-radius:8px;padding:8px;background:white}' +
      'h1{color:' +
      primary +
      ';font-size:22px;flex:1;text-align:center}' +
      'h2{font-size:14px;margin:12px 0 8px;padding:6px 10px;background:#f0f0f0}' +
      '.grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px}' +
      '.item{padding:8px;background:#f9f9f9;border-left:3px solid ' +
      primary +
      '}' +
      '.label{font-size:9px;color:#535353;margin-bottom:3px}.value{font-size:13px;font-weight:bold;color:#000}' +
      '.highlight{background:' +
      success +
      ';color:white;padding:14px;text-align:center;margin:12px 0;border-radius:8px}' +
      '.highlight h2{background:none;color:white;padding:0;margin:0 0 6px 0;font-size:18px}' +
      '.footer{display:flex;justify-content:space-between;align-items:center;margin-top:15px;padding-top:12px;border-top:2px solid #ddd;font-size:10px}' +
      '.qr-section{text-align:center}.qr-label{font-size:10px;color:#535353;margin-bottom:6px;font-weight:600}' +
      '#qrcode{display:inline-block;border:2px solid #ddd;padding:4px;background:white}' +
      '@media print{button{display:none!important}body{padding:0}}' +
      '</style></head><body>' +
      '<div class="header">' +
      (this.selectedMachine.image
        ? '<img src="' + escapeHtml(this.selectedMachine.image) + '" class="product-image" alt="' + title + '">'
        : '<div style="width:120px"></div>') +
      '<h1>ROI Analysis Report</h1><div style="width:120px"></div></div>' +
      '<h2>Machine Information</h2><div class="grid">' +
      '<div class="item"><div class="label">Machine</div><div class="value">' +
      title +
      '</div></div>' +
      '<div class="item"><div class="label">Price per Machine</div><div class="value">' +
      formatCurrency(this.selectedMachine.price) +
      '</div></div>' +
      '<div class="item"><div class="label">Number of Presses</div><div class="value">' +
      params.presses +
      '</div></div>' +
      '<div class="item"><div class="label">Total Machine Cost</div><div class="value">' +
      formatCurrency(results.totalMachineCost) +
      '</div></div>' +
      '<div class="item"><div class="label">Capacity/Hour (each)</div><div class="value">' +
      formatNumber(this.selectedMachine.capacity) +
      ' items</div></div>' +
      '<div class="item"><div class="label">Total Capacity/Hour</div><div class="value">' +
      formatNumber(results.totalCapacityPerHour) +
      ' items</div></div></div>' +
      '<h2>Input Parameters</h2><div class="grid">' +
      '<div class="item"><div class="label">Items to Produce</div><div class="value">' +
      formatNumber(params.items) +
      '</div></div>' +
      '<div class="item"><div class="label">Price per Item</div><div class="value">' +
      formatCurrency(params.price) +
      '</div></div>' +
      '<div class="item"><div class="label">Cost of Goods</div><div class="value">' +
      formatCurrency(params.cogs) +
      '</div></div>' +
      '<div class="item"><div class="label">Labor Cost/Hour</div><div class="value">' +
      formatCurrency(params.labor) +
      '</div></div>' +
      '<div class="item"><div class="label">Work Hours/Day</div><div class="value">' +
      params.hoursPerDay +
      '</div></div></div>' +
      '<h2>Financial Analysis</h2><div class="grid">' +
      '<div class="item"><div class="label">Total Revenue</div><div class="value">' +
      formatCurrency(results.revenue) +
      '</div></div>' +
      '<div class="item"><div class="label">Total COGS</div><div class="value">' +
      formatCurrency(results.totalCogs) +
      '</div></div>' +
      '<div class="item"><div class="label">Labor Cost</div><div class="value">' +
      formatCurrency(results.laborCost) +
      '</div></div>' +
      '<div class="item"><div class="label">Total Machine Cost</div><div class="value">' +
      formatCurrency(results.totalMachineCost) +
      '</div></div>' +
      '<div class="item"><div class="label">Total Costs</div><div class="value">' +
      formatCurrency(results.totalCosts) +
      '</div></div>' +
      '<div class="item"><div class="label">Net Profit</div><div class="value">' +
      formatCurrency(results.profit) +
      '</div></div>' +
      '<div class="item"><div class="label">Net Profit Margin</div><div class="value">' +
      formatNumber(results.margin) +
      '%</div></div>' +
      '<div class="item"><div class="label">Hours to Produce</div><div class="value">' +
      formatNumber(results.hoursNeeded) +
      ' hrs</div></div>' +
      '<div class="item"><div class="label">Days to Produce</div><div class="value">' +
      formatNumber(results.daysNeeded) +
      ' days</div></div>' +
      '<div class="item"><div class="label">Daily Profit</div><div class="value">' +
      formatCurrency(results.dailyProfit) +
      '</div></div></div>' +
      '<div class="highlight"><h2>Time to ROI: ' +
      escapeHtml(roiText) +
      '</h2><p style="font-size:15px;font-weight:600">Daily Profit After ROI: ' +
      formatCurrency(results.dailyProfit) +
      '</p></div>' +
      '<div class="footer"><div style="flex:1"><p style="font-size:10px;color:#535353">Generated on ' +
      new Date().toLocaleDateString() +
      '</p><p style="font-size:10px;color:#535353;margin-top:4px">Visit: www.microtecusa.com</p></div>' +
      '<div class="qr-section"><div class="qr-label">Scan to purchase:</div><div id="qrcode"></div></div></div>' +
      '<button type="button" onclick="window.print()" style="margin-top:15px;padding:10px 20px;background:' +
      primary +
      ';color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:600">Print / Save as PDF</button>' +
      '<script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"><\/script>' +
      '<script>window.addEventListener("load",function(){if(typeof QRCode!=="undefined")new QRCode(document.getElementById("qrcode"),{text:"' +
      productUrl.replace(/"/g, '\\"') +
      '",width:120,height:120,colorDark:"#000000",colorLight:"#ffffff",correctLevel:QRCode.CorrectLevel.H});});<\/script>' +
      '</body></html>';

    var w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) {
      this.setStatus('Popup blocked — allow popups to download the ROI report.', true);
      return;
    }
    w.document.write(html);
    w.document.close();
    this.setStatus('Report opened in a new tab. Use Print → Save as PDF.');
  };

  function initAll() {
    var roots = document.querySelectorAll('[data-roi-calc]:not([data-roi-ready])');
    roots.forEach(function (root) {
      root.setAttribute('data-roi-ready', 'true');
      new RoiCalculatorApp(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor section reloads
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);

  window.RoiCalculatorApp = RoiCalculatorApp;
})();
